import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Play, Square, Volume2, VolumeX, User, Users, FileText, Target, Zap, AlertTriangle, ArrowLeft, Check } from 'lucide-react';
import { Conversation } from '@elevenlabs/client';
import { useKiprData } from '@/lib/kiprData';
import { isLearnerKipPlayable, catalogStatusLabel } from '@/lib/catalogAvailability';

interface KipPlayerProps {
  kipId?: string;
  onDemoComplete?: () => void;
}

type ElevenLabsConversation = Awaited<ReturnType<typeof Conversation.startSession>>;

const KipPlayer: React.FC<KipPlayerProps> = ({ kipId: propKipId, onDemoComplete }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  // Use prop kipId if provided, otherwise use URL param
  const id = propKipId || paramId;
  const {
    kips,
    agents,
    guardians,
    loading,
    hasTrainingAccess,
    billingStatus,
    trainingBalance,
    isAdmin,
    clients,
    colleagues,
    createSession,
    updateSession,
    getElevenLabsSignedUrl,
    recordConversationTurn,
    recordConversationEvent,
    generateReport,
  } = useKiprData();

  const kip = kips.find(k => k.id === id);
  const canPlay = kip ? isLearnerKipPlayable(kip, clients, colleagues, isAdmin) : false;
  const agent = agents.find(a => a.id === kip?.agentId);
  const guardian = guardians.find(g => g.id === kip?.guardianId);
  
  // Conversation state
  const [connectionStatus, setConnectionStatus] = useState<'disconnected'|'connecting'|'connected'>('disconnected');
  const [mode, setMode] = useState<'listening'|'speaking'>('listening');
  const [messages, setMessages] = useState<
    Array<{ type: 'user'|'agent'|'system'; content: string; timestamp: Date }>
  >([]);
  const [isMuted, setIsMuted] = useState(false);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [reportPhase, setReportPhase] = useState<'idle' | 'processing' | 'ready' | 'error'>('idle');
  const [generatedReportId, setGeneratedReportId] = useState<string | null>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [processingStep, setProcessingStep] = useState(0);
  const [mobileView, setMobileView] = useState<'context' | 'conversation'>('context');
  const [recordTab, setRecordTab] = useState<'session' | 'guardian' | 'client'>('session');
  // Refs
  const convoRef = useRef<ElevenLabsConversation | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartedAtRef = useRef<Date | null>(null);
  const orchestrationRequestIdRef = useRef<string | null>(null);

  // Helpers
  const addMessage = (type: 'user'|'agent'|'system', content: string) => {
    const timestamp = new Date();
    setMessages(m => [...m, { type, content, timestamp }]);
    if (sessionIdRef.current && kip) {
      if (type === 'system') {
        void recordConversationEvent({
          sessionId: sessionIdRef.current,
          kipId: kip.id,
          eventName: 'system_message',
          content,
          source: 'system',
          requestId: orchestrationRequestIdRef.current ?? undefined,
          metadata: {
            occurredAt: timestamp.toISOString(),
          },
        }).catch(console.error);
        return;
      }

      void recordConversationTurn({
        sessionId: sessionIdRef.current,
        kipId: kip.id,
        role: type === 'agent' ? 'assistant' : 'user',
        content,
        source: 'elevenlabs',
        requestId: orchestrationRequestIdRef.current ?? undefined,
        metadata: {
          occurredAt: timestamp.toISOString(),
        },
      }).catch(console.error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  useEffect(() => {
    if (recordTab === 'guardian' && !guardian) {
      setRecordTab('session');
      return;
    }
    if (recordTab === 'client' && !kip?.client) {
      setRecordTab('session');
    }
  }, [guardian, kip, recordTab]);

  // Start conversation
  const startConversation = async () => {
    if (!kip || !agent) {
      addMessage('system', 'No agent configured for this scenario');
      return;
    }
    if (!hasTrainingAccess) {
      addMessage(
        'system',
        `You do not have enough training hours to start this call (status: ${billingStatus.replace(/_/g, ' ')}, remaining: ${trainingBalance.remainingHours.toFixed(2)} hours).`,
      );
      return;
    }

    try {
      setConnectionStatus('connecting');
      addMessage('system', 'Connecting to training session...');
      const sessionId = await createSession({ kipId: kip.id, status: 'connecting' });
      sessionIdRef.current = sessionId;
      sessionStartedAtRef.current = new Date();
      if (sessionId) {
        void recordConversationEvent({
          sessionId,
          kipId: kip.id,
          eventName: 'session_start_requested',
          source: 'ui',
          metadata: {
            agentId: agent.id,
          },
        }).catch(console.error);
      }

      // Get microphone permission
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setMediaStream(stream);

      if (!sessionId) {
        throw new Error('Unable to create a session for this call');
      }

      const signedSession = await getElevenLabsSignedUrl({
        sessionId,
        kipId: kip.id,
        agentId: agent.id,
      });
      if (!signedSession?.signedUrl) {
        throw new Error('Unable to secure a signed voice session URL');
      }
      orchestrationRequestIdRef.current = signedSession.requestId || null;

      // Start ElevenLabs conversation
      const conversation = await Conversation.startSession({
        signedUrl: signedSession.signedUrl,
        connectionType: 'websocket',
        onStatusChange: (status) => {
          setConnectionStatus(status.status);
          if (sessionIdRef.current) {
            void recordConversationEvent({
              sessionId: sessionIdRef.current,
              kipId: kip.id,
              eventName: 'connection_status_changed',
              source: 'elevenlabs',
              requestId: orchestrationRequestIdRef.current ?? undefined,
              metadata: {
                status: status.status,
              },
            }).catch(console.error);
          }
          if (status.status === 'connected') {
            addMessage('system', 'Training call started. Begin speaking naturally.');
            if (sessionIdRef.current) {
              void updateSession(sessionIdRef.current, {
                status: 'active',
                startedAt: sessionStartedAtRef.current?.toISOString() ?? new Date().toISOString(),
              });
            }
          }
        },
        onModeChange: (info) => setMode(info.mode),
        onMessage: (evt) => {
          addMessage(evt.role === 'user' ? 'user' : 'agent', evt.text);
        },
        onError: (err) => {
          console.error(err);
          if (sessionIdRef.current) {
            void recordConversationEvent({
              sessionId: sessionIdRef.current,
              kipId: kip.id,
              eventName: 'voice_error',
              source: 'elevenlabs',
              requestId: orchestrationRequestIdRef.current ?? undefined,
              metadata: {
                message: err.message || String(err),
              },
            }).catch(console.error);
          }
          addMessage('system', `Error: ${err.message || err}`);
          setConnectionStatus('disconnected');
        },
      });

      convoRef.current = conversation;
      if (sessionId) {
        await updateSession(sessionId, {
          elevenlabsConversationId: conversation.connection.conversationId,
        });
      }
    } catch (err: unknown) {
      console.error(err);
      setConnectionStatus('disconnected');
      const message = err instanceof Error ? err.message : String(err);
      addMessage('system', `Failed to start call: ${message}`);
      if (sessionIdRef.current) {
        void recordConversationEvent({
          sessionId: sessionIdRef.current,
          kipId: kip.id,
          eventName: 'session_start_failed',
          source: 'ui',
          requestId: orchestrationRequestIdRef.current ?? undefined,
          metadata: { message },
        }).catch(console.error);
        await updateSession(sessionIdRef.current, {
          status: 'failed',
          errorMessage: message,
        }).catch(console.error);
      }
      
      // Clean up stream if connection failed
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
        setMediaStream(null);
      }
    }
  };

  // End conversation
  const endConversation = async () => {
    if (convoRef.current) {
      await convoRef.current.endSession();
      convoRef.current = null;
    }
    
    // Stop microphone
    if (mediaStream) {
      mediaStream.getTracks().forEach(track => track.stop());
      setMediaStream(null);
    }
    
    setConnectionStatus('disconnected');
    addMessage('system', 'Training session ended');
    if (sessionIdRef.current) {
      const endedAt = new Date();
      const durationSeconds = sessionStartedAtRef.current
        ? Math.round((endedAt.getTime() - sessionStartedAtRef.current.getTime()) / 1000)
        : null;

      await updateSession(sessionIdRef.current, {
        status: 'completed',
        endedAt: endedAt.toISOString(),
        durationSeconds,
      });
      void recordConversationEvent({
        sessionId: sessionIdRef.current,
        kipId: kip.id,
        eventName: 'session_ended',
        source: 'ui',
        requestId: orchestrationRequestIdRef.current ?? undefined,
        metadata: {
          durationSeconds,
        },
      }).catch(console.error);
    }
  };

  // Toggle mute
  const toggleMute = async () => {
    if (!convoRef.current) return;
    const newMuted = !isMuted;
    setIsMuted(newMuted);
    await convoRef.current.setVolume({ volume: newMuted ? 0 : 1 });
    addMessage('system', newMuted ? 'Audio muted' : 'Audio unmuted');
  };

  const startReportGeneration = async () => {
    if (!kip) return;

    if (onDemoComplete) {
      onDemoComplete();
      return;
    }

    setReportPhase('processing');
    setReportError(null);
    setProcessingStep(0);

    const t1 = window.setTimeout(() => setProcessingStep(1), 2000);
    const t2 = window.setTimeout(() => setProcessingStep(2), 4000);

    try {
      const reportId = sessionIdRef.current ? await generateReport(sessionIdRef.current, kip.id) : null;
      if (!reportId) {
        throw new Error('The report is still processing. Please try again in a moment.');
      }
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      setProcessingStep(3);
      setGeneratedReportId(reportId);
      setReportPhase('ready');
    } catch (err) {
      console.error(err);
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      setGeneratedReportId(null);
      setReportError(err instanceof Error ? err.message : 'Unable to generate report right now.');
      setReportPhase('error');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (convoRef.current) {
        convoRef.current.endSession();
      }
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [mediaStream]);

  if (loading) {
    return <div className="fixed inset-0 bg-[#FAFAFA] z-50 flex items-center justify-center text-[#555555]">Loading training session...</div>;
  }

  if (!kip) {
    return (
      <div className="fixed inset-0 bg-[#FAFAFA] z-50 flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <h1 className="text-2xl font-semibold font-poppins text-[#1A1A1A]">Scenario not found</h1>
          <p className="mt-2 text-[#555555]">
            This training scenario may have been removed or the link is invalid.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/library')}
              className="inline-flex items-center justify-center rounded-md bg-[#121212] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#2A2A2A] transition-colors"
            >
              Go to library
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center rounded-md border border-[#DADADA] bg-white px-5 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!propKipId && !canPlay) {
    return (
      <div className="fixed inset-0 bg-[#FAFAFA] z-50 flex items-center justify-center px-6">
        <div className="max-w-lg text-center">
          <h1 className="text-2xl font-semibold font-poppins text-[#1A1A1A]">Scenario not available</h1>
          <p className="mt-2 text-[#555555]">
            {kip.availability !== 'available'
              ? `This scenario is ${catalogStatusLabel(kip.availability).toLowerCase()}.`
              : 'This scenario is not open for training yet.'}
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/personas')}
              className="inline-flex items-center justify-center rounded-md bg-[#121212] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#2A2A2A] transition-colors"
            >
              Browse personas
            </button>
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center justify-center rounded-md border border-[#DADADA] bg-white px-5 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] transition-colors"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#FAFAFA] z-50 flex flex-col">
      {/* Header */}
      <div className="bg-white px-4 py-3 sm:px-6 sm:py-4 border-b border-[#DADADA] shadow-sm">
        <div>
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#555555] hover:text-[#1A1A1A] transition-colors mb-1.5 sm:mb-2"
          >
            <ArrowLeft size={16} />
            <span className="text-sm">Back to Dashboard</span>
          </button>
          <h1 className="text-[#1A1A1A] font-semibold font-poppins text-base sm:text-lg leading-tight">{kip.title}</h1>
          <p className="text-[#555555] text-xs sm:text-sm mt-0.5">{agent?.name || 'Training Session'}</p>
        </div>
        <div className="md:hidden mt-3 rounded-lg border border-[#C6AF7C] bg-[#E7D3AB] p-1 grid grid-cols-2 gap-1">
          <button
            type="button"
            onClick={() => setMobileView('context')}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mobileView === 'context'
                ? 'bg-[#FDF8EE] text-[#1A1A1A] shadow-sm'
                : 'text-[#5B4A2A] hover:text-[#1A1A1A]'
            }`}
          >
            Context
          </button>
          <button
            type="button"
            onClick={() => setMobileView('conversation')}
            className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              mobileView === 'conversation'
                ? 'bg-[#FDF8EE] text-[#1A1A1A] shadow-sm'
                : 'text-[#5B4A2A] hover:text-[#1A1A1A]'
            }`}
          >
            Transcript
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Context Panels (full width on mobile, 2/3 on desktop) */}
        <div className={`flex-1 w-full md:w-2/3 bg-white md:border-r border-[#DADADA] overflow-y-auto ${mobileView === 'conversation' ? 'hidden md:block' : 'block'}`}>
          <div className="p-3 sm:p-4 pb-48 md:pb-4">
            <div className="rounded-xl border border-[#C6AF7C] bg-[#E7D3AB] p-2 sm:p-3 shadow-[0_6px_16px_rgba(80,62,28,0.15)]">
              <div className="flex items-end gap-1.5 overflow-x-auto pb-1">
                <button
                  type="button"
                  onClick={() => setRecordTab('session')}
                  className={`shrink-0 rounded-t-lg px-3 py-2 text-xs sm:text-sm font-medium border border-b-0 transition-all ${
                    recordTab === 'session'
                      ? 'bg-[#FDF8EE] text-[#1A1A1A] border-[#D6C197] shadow-[0_-1px_0_#FDF8EE]'
                      : 'bg-[#D9BE8A] text-[#5B4A2A] border-[#B89D69] hover:bg-[#E4CA98]'
                  }`}
                >
                  Session Brief
                </button>
                {guardian && (
                  <button
                    type="button"
                    onClick={() => setRecordTab('guardian')}
                    className={`shrink-0 rounded-t-lg px-3 py-2 text-xs sm:text-sm font-medium border border-b-0 transition-all ${
                      recordTab === 'guardian'
                        ? 'bg-[#FDF8EE] text-[#1A1A1A] border-[#D6C197] shadow-[0_-1px_0_#FDF8EE]'
                        : 'bg-[#D9BE8A] text-[#5B4A2A] border-[#B89D69] hover:bg-[#E4CA98]'
                    }`}
                  >
                    Guardian Notes
                  </button>
                )}
                {kip.client && (
                  <button
                    type="button"
                    onClick={() => setRecordTab('client')}
                    className={`shrink-0 rounded-t-lg px-3 py-2 text-xs sm:text-sm font-medium border border-b-0 transition-all ${
                      recordTab === 'client'
                        ? 'bg-[#FDF8EE] text-[#1A1A1A] border-[#D6C197] shadow-[0_-1px_0_#FDF8EE]'
                        : 'bg-[#D9BE8A] text-[#5B4A2A] border-[#B89D69] hover:bg-[#E4CA98]'
                    }`}
                  >
                    Client File
                  </button>
                )}
              </div>

              <div className="rounded-b-xl rounded-tr-xl border border-[#D6C197] bg-[#FDF8EE] p-4 sm:p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
                <div className="mb-3 border-b border-dashed border-[#D6C197] pb-3">
                  <div className="text-[11px] uppercase tracking-[0.16em] text-[#8A6E3A]">
                    Patient Intake Folder
                  </div>
                  <div className="mt-1 text-[#1A1A1A] text-sm sm:text-base font-semibold">
                    {recordTab === 'session' && 'Session Briefing Sheet'}
                    {recordTab === 'guardian' && 'Guardian Interaction Notes'}
                    {recordTab === 'client' && 'Client Development Profile'}
                  </div>
                </div>

                {recordTab === 'session' && (
                  <div className="space-y-3 text-xs sm:text-sm">
                    <div className="flex items-center text-[#1A1A1A] font-semibold">
                      <FileText size={16} className="mr-2 text-[#8A6E3A]" />
                      Training Session
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Category:</span>
                      <span className="text-[#1A1A1A] ml-2">{kip.category}</span>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Expected Duration:</span>
                      <span className="text-[#1A1A1A] ml-2">{kip.duration}</span>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Session Objective:</span>
                      <p className="text-[#1A1A1A] text-xs sm:text-sm mt-1 leading-relaxed">{kip.description}</p>
                    </div>
                  </div>
                )}

                {recordTab === 'guardian' && guardian && (
                  <div className="space-y-3 text-xs sm:text-sm">
                    <div className="flex items-center text-[#1A1A1A] font-semibold">
                      <Users size={16} className="mr-2 text-[#8A6E3A]" />
                      Guardian Profile
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Name:</span>
                      <span className="text-[#1A1A1A] ml-2">{guardian.name}</span>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Relationship:</span>
                      <span className="text-[#1A1A1A] ml-2">{guardian.relationship}</span>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Personality Notes:</span>
                      <p className="text-[#1A1A1A] text-xs sm:text-sm mt-1 leading-relaxed">{guardian.personality}</p>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Communication Style:</span>
                      <p className="text-[#1A1A1A] text-xs sm:text-sm mt-1 leading-relaxed">{guardian.communicationStyle}</p>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Primary Concerns:</span>
                      <ul className="mt-1 space-y-1">
                        {guardian.concerns.slice(0, 3).map((concern, i) => (
                          <li key={i} className="text-[#1A1A1A] text-xs sm:text-sm flex items-start">
                            <span className="text-[#8A6E3A] mr-1">•</span>
                            {concern}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}

                {recordTab === 'client' && kip.client && (
                  <div className="space-y-3 text-xs sm:text-sm">
                    <div className="flex items-center text-[#1A1A1A] font-semibold">
                      <User size={16} className="mr-2 text-[#8A6E3A]" />
                      Client Profile
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <span className="text-[#6F5A30] text-xs">Name:</span>
                        <p className="text-[#1A1A1A] text-xs sm:text-sm">{kip.client.name}</p>
                      </div>
                      <div>
                        <span className="text-[#6F5A30] text-xs">Age:</span>
                        <p className="text-[#1A1A1A] text-xs sm:text-sm">{kip.client.age} years</p>
                      </div>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Diagnosis:</span>
                      <p className="text-[#1A1A1A] text-xs sm:text-sm mt-1">{kip.client.diagnosis}</p>
                    </div>
                    <div>
                      <span className="text-[#6F5A30]">Learning Style:</span>
                      <p className="text-[#1A1A1A] text-xs sm:text-sm mt-1">{kip.client.style}</p>
                    </div>
                    <div>
                      <span className="text-[#6F5A30] text-xs flex items-center mb-2">
                        <Target size={12} className="mr-1" />
                        Current Targets
                      </span>
                      <ul className="space-y-1">
                        {kip.client.currentTargets.slice(0, 3).map((target, i) => (
                          <li key={i} className="text-[#1A1A1A] text-xs sm:text-sm flex items-start">
                            <span className="text-green-700 mr-1">•</span>
                            {target}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <span className="text-[#6F5A30] text-xs flex items-center mb-2">
                        <Zap size={12} className="mr-1" />
                        Reinforcers
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {kip.client.reinforcers.slice(0, 4).map((reinforcer, i) => (
                          <span key={i} className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                            {reinforcer}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <span className="text-[#6F5A30] text-xs flex items-center mb-2">
                        <AlertTriangle size={12} className="mr-1" />
                        Triggers
                      </span>
                      <div className="flex flex-wrap gap-1.5">
                        {kip.client.triggers.slice(0, 3).map((trigger, i) => (
                          <span key={i} className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded">
                            {trigger}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Conversation Area - Mobile Panel */}
        <div className={`${mobileView === 'conversation' ? 'flex' : 'hidden'} md:hidden flex-1 flex-col min-w-0 bg-[#FAFAFA]`}>
          {connectionStatus === 'connected' && (
            <div className="flex justify-center pt-4 pb-2">
              <div className="relative">
                <div
                  className={`w-12 h-12 rounded-full transition-all duration-300 ${
                    mode === 'speaking'
                      ? 'bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 shadow-lg'
                      : 'bg-gray-300'
                  }`}
                >
                  {mode === 'speaking' && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 animate-ping opacity-30"></div>
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 animate-pulse opacity-50"></div>
                    </>
                  )}
                  <div
                    className={`absolute inset-1.5 rounded-full transition-all duration-300 ${
                      mode === 'speaking'
                        ? 'bg-white opacity-30 animate-pulse'
                        : 'bg-white opacity-20'
                    }`}
                  ></div>
                </div>
                <div className="text-center mt-2 text-xs text-[#555555]">
                  {mode === 'speaking' ? 'AI Speaking...' : 'Listening'}
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 px-3 pb-52 overflow-y-auto">
            <div className="space-y-3">
              {messages.length === 0 && connectionStatus === 'disconnected' && (
                <div className="text-center py-8">
                  <div className="text-[#1A1A1A] text-base mb-2">Ready to begin your training session</div>
                  <div className="text-[#555555] text-sm">
                    Start the call to practice with {agent?.name || 'the AI client'}.
                  </div>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : msg.type === 'system' ? 'justify-center' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[90%] px-3 py-2.5 rounded-lg text-sm ${
                      msg.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : msg.type === 'agent'
                        ? 'bg-white text-[#1A1A1A] border border-[#DADADA] shadow-sm'
                        : 'bg-[#F5F5F5] text-[#555555] text-center border border-[#DADADA]'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-[11px] opacity-75">
                        {msg.type === 'user' ? 'You' : msg.type === 'agent' ? agent?.name?.split(' ')[0] || 'Agent' : 'System'}
                      </span>
                      <span className="text-[11px] opacity-50 ml-2">
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div>{msg.content}</div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>

        {/* Conversation Area - Right Side Panel (hidden on mobile, 1/3 width on desktop) */}
        <div className="hidden md:flex w-1/3 flex-col min-w-0 bg-[#FAFAFA] relative">
          {/* AI Speaking Indicator */}
          {connectionStatus === 'connected' && (
            <div className="flex justify-center py-6">
              <div className="relative">
                {/* Main orb */}
                <div 
                  className={`w-16 h-16 rounded-full transition-all duration-300 ${
                    mode === 'speaking' 
                      ? 'bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 shadow-lg' 
                      : 'bg-gray-300'
                  }`}
                >
                  {/* Pulsing rings when speaking */}
                  {mode === 'speaking' && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 animate-ping opacity-30"></div>
                      <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 animate-pulse opacity-50"></div>
                    </>
                  )}
                  
                  {/* Inner glow */}
                  <div className={`absolute inset-2 rounded-full transition-all duration-300 ${
                    mode === 'speaking' 
                      ? 'bg-white opacity-30 animate-pulse' 
                      : 'bg-white opacity-20'
                  }`}></div>
                </div>
                
                {/* Status text */}
                <div className="text-center mt-3">
                  <div className={`text-sm font-medium transition-colors duration-300 ${
                    mode === 'speaking' ? 'text-purple-600' : 'text-gray-500'
                  }`}>
                    {mode === 'speaking' ? 'AI Speaking...' : 'Listening'}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Messages */}
          <div className="flex-1 p-6 overflow-y-auto">
            <div className="space-y-4">
              {messages.length === 0 && connectionStatus === 'disconnected' && (
                <div className="text-center py-12">
                  <div className="text-[#1A1A1A] text-lg mb-4">Ready to begin your training session</div>
                  <div className="text-[#555555] text-sm mb-8">
                    Click "Start Call" to begin practicing with {agent?.name || 'the AI client'}
                  </div>
                </div>
              )}
              
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.type === 'user' ? 'justify-end' : msg.type === 'system' ? 'justify-center' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs px-4 py-3 rounded-lg ${
                      msg.type === 'user'
                        ? 'bg-blue-600 text-white'
                        : msg.type === 'agent'
                        ? 'bg-white text-[#1A1A1A] border border-[#DADADA] shadow-sm'
                        : 'bg-[#F5F5F5] text-[#555555] text-sm text-center border border-[#DADADA]'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-1">
                      <span className="font-medium text-xs opacity-75">
                        {msg.type === 'user' ? 'You' : msg.type === 'agent' ? agent?.name?.split(' ')[0] || 'Agent' : 'System'}
                      </span>
                      <span className="text-xs opacity-50 ml-2">
                        {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div>{msg.content}</div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Controls */}
          <div className="absolute bottom-0 left-0 right-0 bg-white border-t border-[#DADADA] p-6">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center space-x-4">
                {connectionStatus === 'connected' && (
                  <div className="text-center w-full">
                    <div className="text-[#555555] text-xs">
                      {mode === 'speaking' ? 'Speaking in progress' : 'Ready for your response'}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex flex-col space-y-3">
                {connectionStatus === 'connected' && (
                  <button
                    onClick={toggleMute}
                    className="flex items-center justify-center space-x-2 px-4 py-2 bg-[#F5F5F5] text-[#1A1A1A] border border-[#DADADA] rounded-md hover:bg-[#EEEEEE] transition-colors w-full"
                  >
                    {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    <span>{isMuted ? 'Unmute' : 'Mute'}</span>
                  </button>
                )}
                
                {connectionStatus === 'disconnected' && messages.length === 0 && (
                  <button
                    onClick={startConversation}
                    disabled={connectionStatus === 'connecting'}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium w-full"
                  >
                    <Play size={16} />
                    <span>Start Call</span>
                  </button>
                )}
                
                {connectionStatus === 'connected' && (
                  <button
                    onClick={endConversation}
                    disabled={connectionStatus === 'connecting'}
                    className="flex items-center justify-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium w-full"
                  >
                    <Square size={16} />
                    <span>End Call</span>
                  </button>
                )}
                
                {connectionStatus === 'disconnected' && messages.length > 0 && (
                  <div className="flex flex-col space-y-3">
                    <div className="text-center text-[#555555] text-sm">
                      Call ended - review your performance
                    </div>
                    <button
                      onClick={() => void startReportGeneration()}
                      className="flex items-center justify-center space-x-2 px-6 py-3 bg-[#121212] text-white rounded-md font-medium hover:bg-[#2A2A2A] transition-colors w-full"
                    >
                      <span>{onDemoComplete ? 'Continue' : 'End Session'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Session Processing / Report Ready Overlay */}
      {reportPhase !== 'idle' && (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-gradient-to-br from-[#080810] via-[#10101f] to-[#0c0c18]">
          {/* Subtle background texture */}
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }} />

          <div className="relative z-10 text-center px-8 max-w-md w-full">
            {/* Orb */}
            <div className="flex justify-center mb-12">
              <div className="relative">
                {reportPhase === 'processing' ? (
                  <>
                    {/* Outer glow rings */}
                    <div className="absolute -inset-8 rounded-full bg-violet-500/10 animate-pulse" />
                    <div className="absolute -inset-4 rounded-full bg-violet-500/15 animate-pulse" style={{ animationDelay: '0.3s' }} />
                    {/* Main orb */}
                    <div className="relative w-28 h-28 rounded-full bg-gradient-to-br from-violet-500 via-purple-600 to-blue-600 shadow-[0_0_60px_rgba(139,92,246,0.5)]">
                      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-400 via-purple-500 to-blue-500 animate-ping opacity-30" />
                      <div className="absolute inset-3 rounded-full bg-white/10" />
                      {/* Spinning inner ring */}
                      <div className="absolute inset-2 rounded-full border-2 border-white/20 border-t-white/70 animate-spin" />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="absolute -inset-6 rounded-full bg-emerald-500/15 animate-pulse" />
                    <div className="w-28 h-28 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 shadow-[0_0_60px_rgba(52,211,153,0.45)] flex items-center justify-center">
                      <Check size={48} className="text-white" strokeWidth={2.5} />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Heading & subtext */}
            {reportPhase === 'processing' ? (
              <>
                <h2 className="text-white text-2xl font-semibold font-poppins mb-3 tracking-tight">
                  Analyzing Your Session
                </h2>
                <p className="text-white/40 text-sm mb-12 leading-relaxed">
                  We&apos;re reviewing your conversation and building<br />your personalized performance report.
                </p>
              </>
            ) : reportPhase === 'ready' ? (
              <>
                <h2 className="text-white text-2xl font-semibold font-poppins mb-3 tracking-tight">
                  Your Report is Ready
                </h2>
                <p className="text-white/40 text-sm mb-12 leading-relaxed">
                  Your session has been analyzed. Review your<br />performance insights whenever you&apos;re ready.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-white text-2xl font-semibold font-poppins mb-3 tracking-tight">
                  Report Generation Failed
                </h2>
                <p className="text-white/40 text-sm mb-12 leading-relaxed">
                  {reportError ?? 'We could not generate your report yet. Please retry.'}
                </p>
              </>
            )}

            {/* Progress steps */}
            <div className="space-y-4 mb-12">
              {[
                'Transcribing your conversation',
                'Analyzing communication patterns',
                'Generating your performance report',
              ].map((label, idx) => {
                const isDone = reportPhase === 'ready' || (reportPhase === 'processing' && processingStep > idx);
                const isActive = reportPhase === 'processing' && processingStep === idx;
                return (
                  <div key={idx} className="flex items-center space-x-4">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                      isDone
                        ? 'bg-emerald-500 shadow-[0_0_12px_rgba(52,211,153,0.5)]'
                        : isActive
                        ? 'bg-violet-500 animate-pulse shadow-[0_0_12px_rgba(139,92,246,0.5)]'
                        : 'bg-white/8 border border-white/10'
                    }`}>
                      {isDone && <Check size={14} className="text-white" strokeWidth={3} />}
                      {isActive && <div className="w-2 h-2 rounded-full bg-white" />}
                    </div>
                    <span className={`text-sm text-left transition-colors duration-500 ${
                      isDone ? 'text-white/80' : isActive ? 'text-violet-300' : 'text-white/25'
                    }`}>
                      {label}
                    </span>
                    {isActive && (
                      <div className="ml-auto flex space-x-0.5">
                        {[0, 1, 2].map(i => (
                          <div
                            key={i}
                            className="w-1 h-1 rounded-full bg-violet-400 animate-bounce"
                            style={{ animationDelay: `${i * 0.15}s` }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* CTA — only shown when ready */}
            {reportPhase === 'ready' && generatedReportId && (
              <button
                onClick={() => navigate(`/report/${generatedReportId}`)}
                className="w-full px-8 py-4 bg-gradient-to-r from-violet-600 to-blue-600 text-white font-semibold rounded-xl hover:from-violet-500 hover:to-blue-500 transition-all duration-200 shadow-[0_4px_30px_rgba(139,92,246,0.35)] text-base tracking-wide"
              >
                View Your Report →
              </button>
            )}
            {reportPhase === 'error' && (
              <div className="space-y-3">
                <button
                  onClick={() => void startReportGeneration()}
                  className="w-full px-8 py-4 bg-gradient-to-r from-violet-600 to-blue-600 text-white font-semibold rounded-xl hover:from-violet-500 hover:to-blue-500 transition-all duration-200 shadow-[0_4px_30px_rgba(139,92,246,0.35)] text-base tracking-wide"
                >
                  Retry Report Generation
                </button>
                <button
                  onClick={() => setReportPhase('idle')}
                  className="w-full px-8 py-3 border border-white/30 text-white rounded-xl text-sm font-medium hover:bg-white/10 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Call Controls - Sticky Footer */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-[#DADADA] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-lg z-40">
        <div className="flex flex-col space-y-3">
          {connectionStatus === 'connected' && (
            <div className="flex space-x-3">
              <button
                onClick={toggleMute}
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-[#F5F5F5] text-[#1A1A1A] border border-[#DADADA] rounded-md hover:bg-[#EEEEEE] transition-colors"
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                <span>{isMuted ? 'Unmute' : 'Mute'}</span>
              </button>
              <div className="flex-1 text-center flex items-center justify-center">
                <div className="text-[#555555] text-xs">
                  {mode === 'speaking' ? 'AI Speaking...' : 'Listening'}
                </div>
              </div>
            </div>
          )}
          
          {connectionStatus === 'disconnected' && messages.length === 0 && (
            <button
              onClick={startConversation}
              disabled={connectionStatus === 'connecting'}
              className="flex items-center justify-center space-x-2 px-6 py-4 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium w-full"
            >
              <Play size={16} />
              <span>Start Call</span>
            </button>
          )}
          
          {connectionStatus === 'connected' && (
            <button
              onClick={endConversation}
              disabled={connectionStatus === 'connecting'}
              className="flex items-center justify-center space-x-2 px-6 py-4 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium w-full"
            >
              <Square size={16} />
              <span>End Call</span>
            </button>
          )}
          
          {connectionStatus === 'disconnected' && messages.length > 0 && (
            <div className="flex flex-col space-y-3">
              <div className="text-center text-[#555555] text-sm">
                Call ended - review your performance
              </div>
              <button
                onClick={() => void startReportGeneration()}
                className="flex items-center justify-center space-x-2 px-6 py-4 bg-[#121212] text-white rounded-md font-medium hover:bg-[#2A2A2A] transition-colors w-full"
              >
                <span>{onDemoComplete ? 'Continue' : 'End Session'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default KipPlayer;